import { panel, PropertiesPanel, PropertiesPanelPickerOption, PropertiesPanelSection } from '../../../../../widgets/propertiesPanel';
import { Spinner } from '../../../../../widgets/spinner';
import { useDialogManager } from '../../../../../widgets/dialog';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMaterialsPropertiesCreator } from 'src/components/pages/preview3D/editors/editMaterialsEditor/editMaterialsEditorConfig';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import { GhostButton } from '../../../../../widgets/buttons';
import InspectorShell, { InspectorUsage } from './inspectorShell';
import { CamerasIcon, MeshNodeIcon } from '../../../../../widgets/icons/scene';
import { showErrorNotification } from '../../../../../widgets/notificationToaster';
import { captureMaterialPreview, readMaterialPreview, saveMaterialPreview } from './materialPreview';
import { ensureAbsolute } from './sceneUrls';
import { buildPbrMaterialPanel } from './buildPbrMaterialPanel';
import { buildPbrExtraProperties } from './pbrExtraProperties';
import { ChevronIcon } from '../../../../../widgets/icons/scene';

const generateTextureUniqueId = <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
    return [material.id, type, mobile ? 'mobile' : 'default'].join('_');
};

interface MaterialOverlaySimpleProps {
    material: any; // Il materiale BABYLON selezionato
    onMaterialUpdated?: (material: any) => void; // Callback per notificare aggiornamenti del materiale
    onNameChanged?: (newName: string) => void; // Callback per notificare cambio nome specificatamente
    additionalFeatures?: any;
    onClose?: () => void; // Callback per chiudere l'overlay
    /** L'anteprima è appena stata riscattata: la lista che ci ha aperti può mostrarla subito. */
    onPreviewUpdated?: (materialID: string, dataUrl: string) => void;
    /** Porta la colonna di sinistra sugli oggetti e apre quello scelto: vedi la pastiglia. */
    onRevealObject?: (meshID: string) => void;
}

const UnsupportedNote = styled.p`
    padding: 20px;
    color: ${(props) => props.theme.app.primary};
    font-size: 12px;
    font-style: italic;
    text-align: center;
`;

const PreviewRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;

    button > svg {
        width: 16px;
        height: 16px;
        margin: 0;
    }
`;

const PreviewThumb = styled.span<{ $image: string | null }>`
    flex: none;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    background-image: ${(props) => (props.$image ? `url(${props.$image})` : 'none')};
    background-position: center;
    background-size: cover;
`;

const UpgradeBanner = styled(UpgradeDialog)`
    margin-bottom: 20px;
    p {
        margin: 0;
        padding: 0;
        line-height: 18px;
    }
    z-index: 10000 !important;
`;

// Il titolo di una SEZIONE non si apre e non si chiude (è la regola del prototipo): una sezione
// che si chiude nasconderebbe metà materiale dietro una maniglia che nessuno ha ragione di
// premere. A chiudersi sono i gruppi dentro di lei, che è il livello a cui la scelta serve.
// IL CAPITOLO NON SI DISEGNA PIÙ QUI. Era una `h4` scritta a mano, uguale a quella che World
// ed Effects si scrivevano per conto loro: tre copie della stessa parola in alto, con tre
// margini che avevano già cominciato a divergere. Ora è `PropertiesPanelSection`.

// LA SPINA «ADVANCED» NON È UN ACCORDION, ED È UNA SCELTA DI SOSTANZA. L'accordion di questo
// backoffice tiene il corpo dentro un contenitore alto al massimo 3000px con `overflow: hidden`:
// dieci gruppi aperti là dentro si troverebbero il fondo tagliato, senza niente che lo dica.
// Qui la spina decide **se** i gruppi vengono disegnati, e chi li disegna è la stessa colonna
// che disegna Surface — nessun contenitore in mezzo, nessun tetto da rispettare.
// «ADVANCED» E UN CAPITOLO CHE SI APRE, e si legge come gli altri capitoli: stessa misura,
// stesso peso, stesso grigio. Prima era una riga in grassetto sopra una linea, cioe un terzo
// livello di titolo in una colonna che ne ha gia due — e chi scorreva non sapeva se stava
// leggendo un capitolo o una card.
const AdvancedSpine = styled.button<{ $open: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    margin-top: 14px;
    border: 0;
    padding: 0 2px 6px;
    background: transparent;
    color: ${panel.inkMuted};
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    cursor: pointer;

    svg {
        width: 10px;
        height: 10px;
        transform: rotate(${(props) => (props.$open ? '90deg' : '0deg')});
        transition: transform 120ms ease;
    }
`;

// SI RICORDA PER MATERIALE, e vive fuori dal componente perché il componente si rimonta a ogni
// cambio di soggetto: tenuto nello stato, «Advanced» si richiuderebbe sotto le dita ogni volta
// che si torna su un materiale che si era appena aperto.
const advancedOpenByMaterial = new Map<string, boolean>();

const MaterialOverlaySimple: React.FC<MaterialOverlaySimpleProps> = ({
    material,
    onMaterialUpdated,
    onNameChanged,
    additionalFeatures,
    onClose,
    onPreviewUpdated,
    onRevealObject
}) => {
    const [zkProduct, zkScene, sceneManager, sceneEditor, designManager] = usePreview3DStore((x) => [
        x.zkProduct,
        x.zkScene,
        x.sceneManager,
        x.sceneEditor,
        x.designManager
    ]);

    // Spostato getTextureUrls dentro il componente per accedere a zkScene
    const getTextureUrls = useCallback(
        (texture: BABYLON.BaseTexture): string[] => {
            const isTextureWithFiles = (texture: BABYLON.BaseTexture): texture is BABYLON.BaseTexture & { _files: string[] } =>
                '_files' in texture && !!texture._files && Array.isArray(texture._files);

            const isTextureWithUrl = (texture: BABYLON.BaseTexture): texture is BABYLON.BaseTexture & { url: string } => 'url' in texture && !!texture.url;

            if (isTextureWithFiles(texture) && texture._files.length > 1) {
                return texture._files;
            }
            if (texture.metadata && texture.metadata.fileUrl) {
                return [texture.metadata.fileUrl];
            }
            if (isTextureWithUrl(texture) && texture.url.startsWith('data:')) {
                return [texture.url];
            }
            if (texture.getInternalTexture && texture.getInternalTexture()?.url) {
                const textureUrl = texture.getInternalTexture()?.url;
                return textureUrl ? [textureUrl] : [];
            }
            if (isTextureWithUrl(texture) && texture.url) {
                // Check if materialMapping exists before accessing its properties
                if (
                    (texture as any).materialMapping &&
                    (texture as any).materialMapping.libraryMaterialID &&
                    (texture as any).materialMapping.libraryMaterialRootUrl
                ) {
                    const materialMapping = (texture as any).materialMapping;
                    const idx = texture.url.indexOf(materialMapping.libraryMaterialID.replace(/-/g, ''));
                    if (idx !== -1) return [materialMapping.libraryMaterialRootUrl + texture.url];
                }
                return [ensureAbsolute(zkScene?.get('rootUrl') || '', texture.url)];
            }
            return [];
        },
        [zkScene]
    );

    const [loadingTextures, setLoadingTextures] = useState<Record<string, boolean>>({});
    const [, setUpdate] = useState(false);
    const materialsPropertiesCreator = useMaterialsPropertiesCreator();
    const { showDialog, closeDialog } = useDialogManager();
    const { ecommerceName, sellerMarketplace, updateSubscription } = useSubscriptionData();

    // L'anteprima del materiale aperto. Si riscatta solo quando qualcuno lo chiede:
    // rigenerarla a ogni modifica di proprietà era il disegno di prima, ed è il motivo per cui
    // l'avevano spenta — ogni scatto smonta e rimonta l'environment della scena, e farlo
    // mentre trascini uno slider è insostenibile.
    const [preview, setPreview] = useState<string | null>(null);
    const [isCapturingPreview, setIsCapturingPreview] = useState(false);

    useEffect(() => {
        setPreview(material?.id ? readMaterialPreview(sceneEditor, material.id) : null);
    }, [material?.id, sceneEditor]);

    // Due passi e non uno: lo scatto si mostra appena c'è, e se il caricamento non riesce lo
    // si dice. L'hai chiesto tu, quindi hai diritto sia di vederlo sia di sapere che non
    // sopravviverà al salvataggio.
    const capturePreview = useCallback(async () => {
        if (!sceneEditor || !material?.id || isCapturingPreview) return;

        setIsCapturingPreview(true);
        const dataUrl = await captureMaterialPreview(sceneEditor, material.id);

        if (!dataUrl) {
            setIsCapturingPreview(false);
            showErrorNotification({ message: T._('Could not generate the material preview.', 'Admin') });
            return;
        }

        setPreview(dataUrl);
        onPreviewUpdated?.(material.id, dataUrl);

        const url = await saveMaterialPreview(sceneEditor, material.id, dataUrl);
        setIsCapturingPreview(false);

        if (!url) showErrorNotification({ message: T._('The preview was generated but could not be saved.', 'Admin') });
    }, [sceneEditor, material, isCapturingPreview, onPreviewUpdated]);

    const renderDebounced = useCallback(
        debounce(() => designManager?.render(), 100),
        [designManager]
    );

    const updateState = useCallback(
        <T extends BABYLON.Material>(material: T, refreshMaterials?: boolean) => {
            const isDesignMaterial = zkProduct
                ?.get('scenes')
                .at(0)
                .get('meshSides')
                ?.toArray()
                .some((meshSide) => meshSide.get('materialID') === material.id);

            // Always re render because the base design material uses the same metallic/roughness of the base material
            if (isDesignMaterial) {
                renderDebounced();
            }

            setUpdate((prev) => !prev);
            sceneEditor?.render();
        },
        [sceneManager, zkProduct, renderDebounced, sceneEditor]
    );

    const isTextureLoading = useCallback(
        <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            return loadingTextures[generateTextureUniqueId(material, type, mobile)];
        },
        [loadingTextures]
    );

    const uploadTexture = useCallback(
        async <T extends BABYLON.Material>(material: T, type: string, mobile: boolean, files: FileList) => {
            if (!sceneEditor) {
                throw new Error('Scene editor is not available');
            }

            if (files.length === 0) {
                throw new Error('No files selected');
            }

            if (type === 'reflection' && !additionalFeatures?.customEnvironmentTexture) {
                // Chiudo l'overlay prima di mostrare la dialog
                if (onClose) {
                    onClose();
                }

                showDialog(
                    'Upgrade_Plan',
                    <UpgradeBanner
                        marketPlace={sellerMarketplace ?? undefined}
                        message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), 'Grow')}
                        onUpgradeCompleted={async () => {
                            updateSubscription();
                        }}
                    />
                );
                return;
            }

            const uniqueId = generateTextureUniqueId(material, type, mobile);
            setLoadingTextures((prev) => ({ ...prev, [uniqueId]: true }));

            try {
                if (mobile) {
                    await sceneEditor.loadMobileTextureFilesAsync(Array.from(files), material, type, false);
                } else {
                    await sceneEditor.loadTextureFilesAsync(Array.from(files), material, type, false);
                }
            } catch (error) {
                console.error(error);
                throw new Error('Failed to upload texture', {
                    cause: error
                });
            } finally {
                setLoadingTextures((prev) => ({ ...prev, [uniqueId]: false }));
                updateState(material);
            }
        },
        [sceneEditor, updateState]
    );

    const removeTexture = useCallback(
        async <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            if (!sceneEditor) {
                throw new Error('Scene editor is not available');
            }

            if (mobile) {
                sceneEditor.removeMaterialMobileTexture(material, type);
            } else {
                sceneEditor.removeMaterialTexture(material, type);
            }

            updateState(material);
        },
        [sceneEditor, updateState]
    );

    // DOVE STA LA VERSIONE MOBILE DI UNA TEXTURE, e sono due posti soli: `metadata.mobileUrl` per
    // un'immagine, `metadata.mobileFiles` per una cube map. Li riempiono `setMobileTextureImage` e
    // `setMobileCubeTextureImages` del motore, e un terzo posto non esiste — per questo la ricerca
    // finisce qui invece di continuare altrove.
    //
    // SI RISOLVE COME QUELLA DESKTOP, ed è il punto: la mappa desktop ha cinque modi di trovare la
    // propria immagine e la mobile ne aveva uno, con una regola tutta sua — «se non comincia per
    // http, incollaci davanti la radice». Un `blob:` o un `data:`, che è la forma di una texture
    // appena caricata e non ancora salvata, diventavano così un indirizzo che non esiste: nessuna
    // anteprima, e nessun modo di capire perché. La radice ora la mette `ensureAbsolute`, che
    // riconosce lo schema — ed è la stessa che risolve la mappa desktop.
    //
    // (Resta aperto di là il caso dei materiali importati dalla libreria, la cui versione mobile
    //  vuole la radice del materiale e non quella della scena: https://www.wrike.com/open.htm?id=1712281262)
    const getMobileTextureUrls = useCallback(
        (texture: any): string[] => {
            const rootUrl = zkScene?.get('rootUrl') || '';
            const files: string[] = texture?.metadata?.mobileFiles ?? [];

            if (files.length > 0) {
                return files.map((file) => ensureAbsolute(rootUrl, file));
            }

            const url: string | undefined = texture?.metadata?.mobileUrl;
            return url ? [ensureAbsolute(rootUrl, url)] : [];
        },
        [zkScene]
    );

    const getTexturePreviewUrl = useCallback(
        <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            if (!sceneEditor || !zkScene) {
                return null;
            }

            const texture = sceneEditor.getMaterialTexture(material, type);
            if (!texture) {
                return null;
            }

            const urls = mobile ? getMobileTextureUrls(texture) : getTextureUrls(texture);

            return urls[0] ?? null;
        },
        [sceneEditor, zkScene, getTextureUrls, getMobileTextureUrls]
    );

    // Funzione stabile per l'onChange del nome materiale
    const handleMaterialNameChange = useCallback(
        (newValue: string) => {
            if (!material) {
                return;
            }

            // Aggiorna il nome direttamente sul materiale BABYLON
            material.name = newValue || 'Material';

            // Chiama updateState per aggiornare l'UI dell'overlay
            updateState(material);

            // Notifica immediatamente il cambiamento del nome al parent
            if (onNameChanged) {
                onNameChanged(material.name);
            }

            // Notifica il sistema che i materiali sono stati aggiornati per sincronizzare l'albero
            if (onMaterialUpdated) {
                setTimeout(() => {
                    onMaterialUpdated(material);
                }, 50); // Small delay to ensure material name is fully updated
            }
        },
        [material, updateState, onMaterialUpdated, onNameChanged]
    );

    // Helpers per il materialsPropertiesCreator
    const helpers = useMemo(() => {
        if (!sceneEditor || !sceneManager) {
            return null;
        }
        return {
            sceneEditor,
            sceneManager,
            updateState,
            isTextureLoading,
            uploadTexture,
            removeTexture,
            getTexturePreviewUrl,
            handleMaterialNameChange
        };
    }, [sceneEditor, sceneManager, updateState, isTextureLoading, uploadTexture, removeTexture, getTexturePreviewUrl, handleMaterialNameChange]);

    // La spina «Advanced» si ricorda di essere aperta, e la memoria è per materiale: aprirla su
    // uno non dice niente su quello che si guarderà dopo.
    const materialKey = material?.id ?? material?.name ?? '';
    const [advancedOpen, setAdvancedOpen] = useState(false);

    useEffect(() => setAdvancedOpen(advancedOpenByMaterial.get(materialKey) ?? false), [materialKey]);

    const toggleAdvanced = useCallback(() => {
        setAdvancedOpen((open) => {
            advancedOpenByMaterial.set(materialKey, !open);
            return !open;
        });
    }, [materialKey]);

    // Funzione per verificare se il materiale è Standard (non supportato)
    const isStandardMaterial = useCallback((material: any) => {
        // Un materiale Standard ha diffuseColor ma NON ha proprietà PBR avanzate
        return (
            material &&
            material.diffuseColor !== undefined &&
            (material.clearCoat === undefined || !material.clearCoat?.isEnabled) &&
            (material.metallic === undefined || material.metallic === 0) &&
            (material.roughness === undefined || material.roughness === 0) &&
            // Oppure verifica il tipo direttamente se disponibile
            (material instanceof BABYLON?.StandardMaterial || material.getClassName?.() === 'StandardMaterial')
        );
    }, []);

    if (!material || !helpers) {
        return (
            <InspectorShell kind='material' name={material?.name ?? ''}>
                <Spinner />
            </InspectorShell>
        );
    }

    const kindOfMaterial = material.getClassName?.() ?? T._('Material', 'Admin');

    // A QUALI OGGETTI È ADDOSSO. È la domanda che ci si fa guardando un materiale — «se lo cambio,
    // che cosa ridipingo?» — e la risposta la sa il motore: `getMeshesUsingMaterial` guarda anche
    // dentro i MultiMaterial, cioè le mesh che ne portano più d'uno e di cui questo è una parte.
    //
    // SI RICONTA A OGNI DISEGNO, e non è uno spreco: è un giro sulle mesh della scena. Una lista
    // tenuta da parte direbbe invece il conto di prima, perché l'assegnazione si cambia da un ALTRO
    // pannello — lo slot di una mesh — e da lì nessuno viene ad avvisare questo.
    //
    // ZERO OGGETTI, NIENTE PASTIGLIA: «0 objects» è la risposta a una domanda che nessuno ha fatto.
    const meshesUsingMaterial: any[] = sceneEditor?.getMeshesUsingMaterial?.(material.id) ?? [];

    const usedBy: InspectorUsage[] | undefined = meshesUsingMaterial.length
        ? [
              {
                  label: stringFormat(
                      meshesUsingMaterial.length === 1 ? T._('{0} object', 'Admin') : T._('{0} objects', 'Admin'),
                      meshesUsingMaterial.length.toString()
                  ),
                  current: meshesUsingMaterial.find((mesh) => sceneEditor?.isMeshSelected?.(mesh))?.id,
                  entries: meshesUsingMaterial.map((mesh): PropertiesPanelPickerOption => {
                      // I TRIANGOLI SONO UNA DIVISIONE, non un dato da chiedere a qualcuno: gli indici
                      // vanno a tre a tre. Ed è il dato che distingue due «Zone» che si chiamano quasi
                      // uguale — quale delle due è il pezzo grande.
                      const indices = typeof mesh.getTotalIndices === 'function' ? mesh.getTotalIndices() : 0;
                      const triangles = indices ? Math.round(indices / 3) : 0;

                      return {
                          value: mesh.id,
                          label: mesh.name,
                          caption: triangles ? `${triangles.toLocaleString()} ${T._('triangles', 'Admin')}` : undefined,
                          mark: <MeshNodeIcon />
                      };
                  }),
                  // ANDARCI SONO DUE MOVIMENTI: la colonna di sinistra torna sugli oggetti e la riga
                  // giusta si apre. Li fa `revealTarget` — lo stesso giro della freccia «dove sta» —
                  // e qui si dice soltanto dove si vuole andare.
                  onPick: (value) => onRevealObject?.(String(value))
              }
          ]
        : undefined;

    // Se è un materiale Standard, mostra solo il messaggio di non supportato
    if (isStandardMaterial(material)) {
        return (
            <InspectorShell kind='material' name={material.name} specs={kindOfMaterial} usage={usedBy} onRename={handleMaterialNameChange}>
                <UnsupportedNote>{T._('Materiale non supportato', 'Admin')}</UnsupportedNote>
            </InspectorShell>
        );
    }

    // LE PROPRIETÀ SONO QUELLE DI SEMPRE, RIMESSE IN ORDINE. `buildPbrMaterialPanel` non le
    // costruisce: prende quelle che il pannello condiviso costruisce già e le colloca nei
    // capitoli. Ciò che la tabella non nomina non compare — vedi pbrMaterialLayout.ts.
    const properties = materialsPropertiesCreator(material, helpers);

    // LA RIORGANIZZAZIONE È DEL SOLO `PBRMaterial` (utente, 2026-08-20). Gli altri due tipi PBR
    // hanno proprietà con altri nomi: passarli da questa tabella non li riorganizzerebbe, li
    // svuoterebbe — nessun id combacia, e resterebbe un pannello vuoto. «Ignorarli» vuol dire
    // lasciarli come sono, quindi si mostra l'elenco piatto di prima.
    const isClassicPbr = material instanceof BABYLON.PBRMaterial;
    const sections = isClassicPbr ? buildPbrMaterialPanel(properties, buildPbrExtraProperties(material as BABYLON.PBRMaterial, helpers)) : [];
    const advancedSections = sections.filter((section) => section.advanced);

    return (
        <InspectorShell kind='material' name={material.name} specs={kindOfMaterial} usage={usedBy} onRename={handleMaterialNameChange}>
            {/* La sfera fotografata è la stessa che la lista mostra nella tessera: qui si
                vede com'è venuta e si può riscattarla dopo aver cambiato il materiale. */}
            <PreviewRow>
                <PreviewThumb $image={preview} aria-hidden='true' />
                <GhostButton type='button' $size='small' onClick={capturePreview} disabled={isCapturingPreview || !sceneEditor}>
                    <CamerasIcon />
                    <span>{isCapturingPreview ? T._('Generating...', 'Admin') : T._('Regenerate preview', 'Admin')}</span>
                </GhostButton>
            </PreviewRow>

            {/* IN EVIDENZA RESTA LA SOLA SURFACE — colore, finitura, trasparenza: ciò che si
                tocca a ogni materiale. Gli altri capitoli entrano interi sotto la spina, e il
                pannello parte con tre gruppi invece che con diciotto. */}
            {!isClassicPbr && <PropertiesPanel properties={properties} />}

            {sections
                .filter((section) => !section.advanced)
                .map((section) => (
                    <PropertiesPanelSection key={section.id} title={section.label}>
                        <PropertiesPanel properties={section.groups} />
                    </PropertiesPanelSection>
                ))}

            {advancedSections.length > 0 && (
                <AdvancedSpine type='button' $open={advancedOpen} aria-expanded={advancedOpen} onClick={toggleAdvanced}>
                    <ChevronIcon />
                    <span>{T._('Advanced', 'Admin')}</span>
                </AdvancedSpine>
            )}

            {advancedOpen &&
                advancedSections.map((section) => (
                    <PropertiesPanelSection key={section.id} title={section.label}>
                        <PropertiesPanel properties={section.groups} />
                    </PropertiesPanelSection>
                ))}
        </InspectorShell>
    );
};

export default MaterialOverlaySimple;
